import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  BookOpen,
  ChevronRight,
  CircleHelp,
  Gift,
  Home,
  ListChecks,
  LogIn,
  LogOut,
  Percent,
  Plus,
  Share2,
  User,
  UserPlus,
  Wallet,
  X,
} from "lucide-react";
import API from "../services/api";
import BrandLogo from "./BrandLogo";
import WhatsAppGlyph from "./WhatsAppGlyph";
import { buildWhatsAppUrl, openExternalLink, shareAppLink } from "../utils/appInteractions";

function Sidebar({ isOpen, setIsOpen }) {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [wallet, setWallet] = useState({ balance: 0, deposit: 0, winnings: 0 });
  const [settings, setSettings] = useState({ whatsapp_number: "" });
  const [assignedAgent, setAssignedAgent] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    API.get(`/wallet/${user.id}`)
      .then((res) => setWallet(res.data || { balance: 0, deposit: 0, winnings: 0 }))
      .catch(() => setWallet({ balance: 0, deposit: 0, winnings: 0 }));
    API.get(`/users/${user.id}/assigned-agent`)
      .then((res) => setAssignedAgent(res.data?.has_agent ? res.data.agent : null))
      .catch(() => setAssignedAgent(null));
  }, [user]);

  useEffect(() => {
    API.get("/settings/public").then((res) => setSettings(res.data || {})).catch(() => {});
  }, []);

  const close = () => setIsOpen(false);

  const goProtected = (path) => {
    close();
    navigate(user ? path : "/login");
  };

  const goPublic = (path) => {
    close();
    navigate(path);
  };

  const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("mk_user_session_active");
    close();
    navigate("/login");
  };

  const openWhatsapp = () => {
    const phone = getWhatsappPhone(settings.whatsapp_number);
    if (!phone) return;
    const url = buildWhatsAppUrl(phone, "Hello Matka Kings support");
    if (!url) return;
    openExternalLink(url);
  };

  const openAgentWhatsapp = () => {
    const phone = getWhatsappPhone(assignedAgent?.whatsapp_number);
    if (!phone) return;
    const agentName = assignedAgent?.display_name || "Agent";
    const url = buildWhatsAppUrl(phone, `Hello ${agentName}`);
    if (!url) return;
    openExternalLink(url);
  };

  const shareApp = async () => {
    const referralCode = String(user?.referral_code || "").trim();
    const url = `${window.location.origin}/login?tab=register${referralCode ? `&ref=${encodeURIComponent(referralCode)}` : ""}`;
    const text = referralCode
      ? `Join Matka Kings with my referral code ${referralCode}`
      : "Join Matka Kings";

    try {
      const shared = await shareAppLink({ title: "Matka Kings", text, url });
      if (!shared) alert("Share link copied");
    } catch (_) {}
  };

  return (
    <>
      {isOpen && <div onClick={close} style={overlay} />}

      <aside style={{ ...drawer, transform: isOpen ? "translateX(0)" : "translateX(-100%)" }}>
        <header style={drawerHeader}>
          <button type="button" style={brand} onClick={() => { close(); navigate("/dashboard"); }}>
            <BrandLogo size="sm" />
          </button>
          <button type="button" style={closeButton} onClick={close}>
            <X size={15} />
          </button>
        </header>

        {user ? (
          <section style={walletCard}>
            <span style={walletLabel}>Wallet Balance</span>
            <strong style={walletAmount}>{`Rs ${Number(wallet.balance || 0).toFixed(2)}`}</strong>
            <div style={walletActions}>
              <button type="button" style={walletPrimary} onClick={() => goProtected("/deposit")}>
                <Plus size={13} strokeWidth={3} /> Add Money
              </button>
              <button type="button" style={walletSecondary} onClick={() => goProtected("/withdraw")}>
                Withdraw
              </button>
            </div>
          </section>
        ) : (
          <section style={welcomeCard}>
            <div style={userIcon}><User size={24} /></div>
            <div>
              <h3 style={welcomeTitle}>Welcome!</h3>
              <p style={welcomeText}>Login to start playing</p>
            </div>
            <div style={authRow}>
              <button type="button" style={loginButton} onClick={() => { close(); navigate("/login"); }}>
                <LogIn size={16} /> Log In
              </button>
              <button type="button" style={signupButton} onClick={() => { close(); navigate("/login?tab=register"); }}>
                <UserPlus size={16} /> Sign Up
              </button>
            </div>
          </section>
        )}

        <nav style={menuList}>
          <MenuItem icon={Home} label="Home" active onClick={() => { close(); navigate("/dashboard"); }} />
          <MenuItem icon={Wallet} label="Wallet" color="#059669" onClick={() => goProtected("/wallet")} />
          {user && assignedAgent ? (
            <AgentMenuItem name={assignedAgent.display_name || "Agent"} onClick={() => { close(); openAgentWhatsapp(); }} />
          ) : null}
          <MenuItem icon={ListChecks} label="My Bids" color="#7c3aed" onClick={() => goProtected("/bids")} />
          <MenuItem icon={BookOpen} label="Passbook" color="#d97706" onClick={() => goProtected("/passbook")} />
          <Divider />
          <MenuItem icon={BarChart3} label="Charts" color="#db2777" onClick={() => goPublic("/charts")} />
          <MenuItem icon={Percent} label="Game Rates" color="#4f46e5" onClick={() => goPublic("/game-rates")} />
          <Divider />
          <MenuItem icon={Gift} label="Refer & Earn" color="#f59e0b" onClick={() => goProtected("/refer")} />
          <MenuItem icon={User} label="My Profile" color="#64748b" onClick={() => goProtected("/profile")} />
          <MenuItem icon={CircleHelp} label="Game Rules" color="#0d9488" onClick={() => goProtected("/game-rules")} />
          {user && (
            <>
              <Divider />
              <MenuItem icon={Share2} label="Share App" color="#2563eb" onClick={shareApp} />
              <MenuItem icon={LogOut} label="Logout" color="#dc2626" onClick={logout} />
            </>
          )}
        </nav>

        <footer style={drawerFooter}>
          <button type="button" style={supportButton} onClick={openWhatsapp}>
            <span style={supportIcon}><WhatsAppGlyph size={20} color="#ffffff" /></span>
            <span style={supportText}>
              <strong>Chat with Support</strong>
              <small>24/7 on WhatsApp</small>
            </span>
            <ChevronRight size={16} color="rgba(255,255,255,0.45)" />
          </button>
          <p style={versionText}>MATKA KINGS v2.0</p>
        </footer>
      </aside>
    </>
  );
}

function MenuItem({ icon: Icon, label, color = "#2563eb", active, onClick }) {
  return (
    <button type="button" style={active ? activeItem : menuItem} onClick={onClick}>
      <span style={{ ...menuIcon, background: active ? "#2563eb" : `${color}16`, color: active ? "#fff" : color }}>
        <Icon size={19} />
      </span>
      <strong style={menuLabel}>{label}</strong>
      <ChevronRight size={15} color={active ? "#2563eb" : "#d1d5db"} />
    </button>
  );
}

function AgentMenuItem({ name, onClick }) {
  return (
    <button type="button" style={agentMenuItem} onClick={onClick}>
      <span style={agentMenuIcon}><WhatsAppGlyph size={19} color="#ffffff" /></span>
      <span style={agentMenuText}>
        <strong>Contact {name}</strong>
        <small>Your personal agent</small>
      </span>
      <ChevronRight size={15} color="#16a34a" />
    </button>
  );
}

function Divider() {
  return <div style={divider} />;
}

function getWhatsappPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  return digits;
}

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 999 };
const drawer = { position: "fixed", inset: "0 auto 0 0", width: "300px", maxWidth: "85vw", height: "100dvh", maxHeight: "100dvh", background: "#fff", color: "#111827", zIndex: 1000, transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1)", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "none" };
const drawerHeader = { height: "48px", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", borderBottom: "1px solid #f3f4f6", flexShrink: 0 };
const brand = { border: 0, background: "transparent", display: "flex", alignItems: "center", padding: 0, fontSize: "15px", fontWeight: 900, letterSpacing: "-0.01em", cursor: "pointer" };
const closeButton = { width: "30px", height: "30px", border: 0, borderRadius: "50%", background: "#f9fafb", color: "#9ca3af", display: "grid", placeItems: "center" };
const walletCard = { margin: "10px 12px", padding: "12px 10px 10px", background: "linear-gradient(135deg,#a16207,#f59e0b,#fcd34d)", color: "#fff", borderRadius: "14px", flexShrink: 0, boxShadow: "0 10px 24px rgba(245,158,11,0.18)" };
const walletLabel = { display: "block", fontSize: "10px", letterSpacing: "0.08em", opacity: 0.82, fontWeight: 700, textTransform: "uppercase", color: "#fef3c7" };
const walletAmount = { display: "block", fontSize: "21px", lineHeight: 1.15, fontWeight: 800, marginTop: "2px" };
const walletActions = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "10px" };
const walletPrimary = { height: "34px", border: 0, borderRadius: "10px", background: "#ffffff", color: "#b45309", fontWeight: 800, fontSize: "11px", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", boxShadow: "0 4px 10px rgba(120,53,15,0.14)" };
const walletSecondary = { ...walletPrimary, background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.22)", boxShadow: "none" };
const welcomeCard = { margin: "10px 12px", padding: "12px 10px", display: "grid", gridTemplateColumns: "42px 1fr", gap: "10px", background: "#f8fafc", borderRadius: "14px", flexShrink: 0 };
const userIcon = { width: "42px", height: "42px", display: "grid", placeItems: "center", borderRadius: "50%", background: "#eff6ff", color: "#60a5fa" };
const welcomeTitle = { margin: 0, fontSize: "14px", fontWeight: 800 };
const welcomeText = { margin: "2px 0 0", color: "#64748b", fontSize: "11px", fontWeight: 600 };
const authRow = { gridColumn: "1 / -1", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" };
const loginButton = { height: "38px", border: 0, borderRadius: "12px", background: "#2563eb", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", fontWeight: 800, fontSize: "12px" };
const signupButton = { ...loginButton, background: "#fff", color: "#111827", border: "1px solid #e5e7eb" };
const menuList = { flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", display: "grid", alignContent: "start", gap: 0, padding: "6px" };
const menuItem = { height: "50px", display: "grid", gridTemplateColumns: "42px 1fr 16px", alignItems: "center", border: 0, background: "#fff", color: "#4b5563", textAlign: "left", borderRadius: "10px", padding: "0 10px" };
const activeItem = { ...menuItem, background: "#f9fafb", color: "#111827", fontWeight: 700 };
const menuIcon = { width: "34px", height: "34px", display: "grid", placeItems: "center", borderRadius: "10px" };
const menuLabel = { fontSize: "14.5px", fontWeight: 800 };
const agentMenuItem = { minHeight: "56px", display: "grid", gridTemplateColumns: "42px 1fr 16px", alignItems: "center", border: "1px solid #bbf7d0", background: "linear-gradient(135deg,#ecfdf5,#fff7ed)", color: "#14532d", textAlign: "left", borderRadius: "12px", padding: "0 10px", margin: "3px 0" };
const agentMenuIcon = { width: "34px", height: "34px", display: "grid", placeItems: "center", borderRadius: "11px", background: "linear-gradient(135deg,#16a34a,#22c55e)", boxShadow: "0 7px 16px rgba(22,163,74,.22)", overflow: "hidden" };
const agentMenuText = { display: "grid", gap: "2px", fontSize: "13px", fontWeight: 950 };
const divider = { height: "1px", background: "#f1f5f9", margin: "4px 10px" };
const drawerFooter = { padding: "8px", borderTop: "1px solid #f3f4f6", flexShrink: 0 };
const supportButton = { width: "100%", border: 0, borderRadius: "14px", background: "linear-gradient(135deg,#25D366 0%, #17b857 100%)", color: "#fff", display: "grid", gridTemplateColumns: "36px 1fr 16px", alignItems: "center", gap: "8px", padding: "9px", boxShadow: "0 10px 24px rgba(8,93,40,0.22)" };
const supportIcon = { width: "36px", height: "36px", borderRadius: "50%", background: "linear-gradient(135deg,#28de6d,#149d4c)", display: "grid", placeItems: "center", overflow: "hidden", border: "2px solid rgba(255,255,255,0.92)", boxShadow: "0 8px 16px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.24)", boxSizing: "border-box" };
const supportText = { display: "grid", gap: "3px", textAlign: "left" };
const versionText = { textAlign: "center", fontSize: "10px", color: "#94a3b8", marginTop: "6px", fontWeight: 700 };

export default Sidebar;
